import React, { useEffect, useMemo, useRef, useState } from "react";
import { headsOfDepartmentsApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import "./heads-of-departments.scss";

const defaultForm = {
  _id: null, Profile_Photo: "", Name: "", Department: "", Qualification: "",
  Phone: "", Email: "", Description: "", Status: "Active",
};

const HeadsOfDepartments = () => {
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchDepartment, setSearchDepartment] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [modalType, setModalType] = useState("");
  const [selectedHead, setSelectedHead] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [confirmId, setConfirmId] = useState(null);
  const [descriptionPreview, setDescriptionPreview] = useState(null);
  const [confirmAction, setConfirmAction] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => { fetchHeads(); }, []);

  const fetchHeads = async () => {
    try {
      setLoading(true);
      const response = await apiClient(headsOfDepartmentsApi);
      const result = await response.json();
      if (response.ok) { setHeads(result.data || []); }
    } catch (error) {
      console.error("Error fetching HODs:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredHeads = useMemo(() => {
    return heads.filter((item) => {
      const matchName = (item.Name || "").toLowerCase().includes(searchName.toLowerCase());
      const matchDept = (item.Department || "").toLowerCase().includes(searchDepartment.toLowerCase());
      return matchName && matchDept;
    });
  }, [heads, searchName, searchDepartment]);

  useEffect(() => {
    const handleOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const closeDropdown = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", closeDropdown, true);
    window.addEventListener("resize", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", closeDropdown, true);
      window.removeEventListener("resize", closeDropdown);
    };
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 160 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => { setFormData(defaultForm); setSelectedFile(null); setPreviewUrl(null); setModalType("add"); setDropdownOpenId(null); };
  const openViewModal = (head) => { setSelectedHead(head); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (head) => { setFormData({ ...head }); setSelectedFile(null); setPreviewUrl(null); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal = () => { setModalType(""); setSelectedHead(null); setFormData(defaultForm); setSelectedFile(null); setPreviewUrl(null); };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setFormData(p => ({ ...p, Profile_Photo: "" }));
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? headsOfDepartmentsApi : `${headsOfDepartmentsApi}/${formData._id}`;
      
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'Profile_Photo' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedFile) {
        formDataToSend.append('Profile_Photo', selectedFile);
      } else if (formData.Profile_Photo) {
        formDataToSend.append('Profile_Photo', formData.Profile_Photo);
      }

      const response = await apiFormDataClient(url, { method, body: formDataToSend });
      if (response.ok) { fetchHeads(); closeModal(); }
    } catch (error) {
      console.error("Error saving HOD:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${headsOfDepartmentsApi}/${id}`, { method: "DELETE" });
      fetchHeads(); setConfirmId(null);
    } catch (error) {
      console.error("Error deleting HOD:", error);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  return (
    <div className="hod-page">
      <div className="hod-page__header">
        <div><h1>Principal of College</h1><p>Manage principal of college, qualifications, and contact information.</p></div>
        <button className="add-hod-btn" type="button" onClick={openAddModal}><span>+</span> Add HOD</button>
      </div>

      <div className="hod-search-card">
        <div className="hod-search-grid">
          <div className="form-group"><label>Search by Name</label><input type="text" placeholder="Enter name" value={searchName} onChange={(e) => setSearchName(e.target.value)} /></div>
          <div className="form-group"><label>Search by Department</label><input type="text" placeholder="Enter department" value={searchDepartment} onChange={(e) => setSearchDepartment(e.target.value)} /></div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="hod-table-card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Photo</th><th>Name</th><th>Department</th><th>Qualification</th><th>Email</th><th>Description</th><th className="actions-col">Actions</th></tr></thead>
            <tbody>
              {filteredHeads.length > 0 ? (
                filteredHeads.map((head) => (
                  <tr key={head._id}>
                    <td><div className="table-photo">{head.Profile_Photo ? <img src={head.Profile_Photo} alt="" /> : <div className="photo-placeholder">{(head.Name || "?").charAt(0)}</div>}</div></td>
                    <td>{head.Name}</td><td>{head.Department}</td><td>{head.Qualification}</td><td>{head.Email}</td>
                    <td><span className={`status-badge ${head.Status}`}>{head.Status === "Active" ? "Active" : "Inactive"}</span></td>
                    <td>
                      {head.Description ? (
                        <button
                          className="desc-preview-btn"
                          type="button"
                          onClick={() => setDescriptionPreview({ name: head.Name, description: head.Description })}
                          title="Preview Description"
                        >
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                          Preview
                        </button>
                      ) : (
                        <span className="no-desc">—</span>
                      )}
                    </td>
                    <td className="actions-cell"><button className="dots-btn" type="button" onClick={(e) => handleDotsClick(e, head._id)}>⋮</button></td>
                  </tr>
                ))
              ) : (<tr><td colSpan="8" className="empty-cell">No records found.</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {dropdownOpenId !== null && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}>
          <button type="button" onClick={() => openViewModal(heads.find(i => i._id === dropdownOpenId))}><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg> View</button>
          <button type="button" onClick={() => openEditModal(heads.find(i => i._id === dropdownOpenId))}><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg> Edit</button>
          <button type="button" className="delete" onClick={() => openConfirmModal(dropdownOpenId, "delete")}><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg> Delete</button>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon"><svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg></div>
            <h3>Delete HOD?</h3><p>Are you sure you want to permanently remove this head of department record?</p>
            <div className="confirm-actions"><button className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button><button className="btn btn-danger" onClick={() => handleDelete(confirmId)}>Delete</button></div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="hod-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{modalType === "add" ? "Add HOD" : "Edit HOD"}</h2><button className="modal-close" type="button" onClick={closeModal}>×</button></div>
            <form className="modal-form" onSubmit={handleSave}>
              <div className="form-section-label">Media</div>
              <div className="form-group">
                <label>Profile Photo</label>
                <div className="photo-upload-box">
                  {(previewUrl || formData.Profile_Photo) ? (
                    <div className="photo-preview-wrapper">
                      <img src={previewUrl || formData.Profile_Photo} alt="Preview" className="photo-preview" />
                      <div className="photo-actions">
                        <label className="photo-change-btn">Change Photo<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                        <button type="button" className="photo-remove-btn" onClick={removePhoto}>Remove Photo</button>
                      </div>
                    </div>
                  ) : (
                    <div className="photo-upload-placeholder">
                      <label className="file-upload-label">
                        <div className="upload-icon">📷</div>
                        <span>Click to upload photo</span>
                        <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                      </label>
                      <small>JPG, PNG, WEBP supported</small>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-section-label">Basic Info</div>
              <div className="form-row">
                <div className="form-group"><label>Full Name *</label><input type="text" value={formData.Name} onChange={e => setFormData({...formData, Name: e.target.value})} required /></div>
                <div className="form-group"><label>Department *</label><input type="text" value={formData.Department} onChange={e => setFormData({...formData, Department: e.target.value})} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Qualification</label><input type="text" value={formData.Qualification} onChange={e => setFormData({...formData, Qualification: e.target.value})} /></div>
                <div className="form-group"><label>Phone Number</label><input type="text" value={formData.Phone} onChange={e => setFormData({...formData, Phone: e.target.value})} /></div>
              </div>
              <div className="form-group"><label>Email *</label><input type="email" value={formData.Email} onChange={e => setFormData({...formData, Email: e.target.value})} required /></div>
              <div className="form-group"><label>Description</label><textarea value={formData.Description} onChange={e => setFormData({...formData, Description: e.target.value})} rows="3" /></div>
              <div className="modal-actions"><button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button><button type="submit" className="btn btn-primary">Save HOD</button></div>
            </form>
          </div>
        </div>
      )}

      {modalType === "view" && selectedHead && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="hod-modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>View HOD Profile</h2><button className="modal-close" type="button" onClick={closeModal}>×</button></div>
            <div className="view-body">
              <div className="view-profile-top">{selectedHead.Profile_Photo ? <img src={selectedHead.Profile_Photo} alt="" className="view-photo" /> : <div className="view-photo-placeholder">{(selectedHead.Name || "?").charAt(0)}</div>}<div className="view-profile-info"><h3>{selectedHead.Name}</h3><span className="view-dept">{selectedHead.Department}</span></div></div>
              <div className="view-section-label">Contact & Academic Info</div>
              <div className="view-grid"><div className="view-item"><span>Email</span><strong>{selectedHead.Email || "-"}</strong></div><div className="view-item"><span>Phone</span><strong>{selectedHead.Phone || "-"}</strong></div><div className="view-item full"><span>Qualification</span><strong>{selectedHead.Qualification || "-"}</strong></div><div className="view-item full"><span>Description</span><strong>{selectedHead.Description || "-"}</strong></div></div>
              <div className="view-footer-actions"><button type="button" className="btn btn-light" onClick={closeModal}>Close</button><button type="button" className="btn btn-primary" onClick={() => { setFormData({...selectedHead}); setModalType("edit"); }}>Edit HOD</button></div>
            </div>
          </div>
        </div>
      )}

      {/* Description Preview Modal */}
      {descriptionPreview && (
        <div className="modal-overlay" onClick={() => setDescriptionPreview(null)}>
          <div className="desc-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="desc-preview-modal__header">
              <div className="desc-preview-modal__title">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                <span>Description Preview</span>
              </div>
              <button className="modal-close" type="button" onClick={() => setDescriptionPreview(null)}>×</button>
            </div>
            <div className="desc-preview-modal__person">{descriptionPreview.name}</div>
            <div className="desc-preview-modal__body">
              <p>{descriptionPreview.description}</p>
            </div>
            <div className="desc-preview-modal__footer">
              <button className="btn btn-light" onClick={() => setDescriptionPreview(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadsOfDepartments;